import { createFileRoute, redirect, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { isForecastEnabled, isXeroEnabled } from '@/lib/feature-flags';
import { FinanceGuard } from '@/components/finance/FinanceGuard';
import { FinanceNav } from '@/components/tracking/FinanceNav';
import { FinanceSlider } from '@/components/finance/FinanceSlider';
import { staffMix } from '@/lib/financial-calc/forecast';
import { fmtCurrency } from '@/components/tracking/format';
import { ForecastToolNav } from '@/components/finance/ForecastToolNav';

/**
 * /finance/forecast/staff-mix — the Staff Mix tool (F-013 S6.7). Site (billable) vs office (overhead)
 * headcount → net contribution + the profit swing from converting one office role to site. A calculator.
 */
export const Route = createFileRoute('/finance/forecast_/staff-mix')({
  beforeLoad: () => {
    if (!isXeroEnabled() || !isForecastEnabled()) {
      throw redirect({ to: '/finance', replace: true });
    }
  },
  component: () => (
    <FinanceGuard>
      <StaffMix />
    </FinanceGuard>
  ),
});


function StaffMix() {
  const [site, setSite] = useState(8);
  const [office, setOffice] = useState(3);
  const [gpHead, setGpHead] = useState(120_000);
  const [costHead, setCostHead] = useState(80_000);

  const m = staffMix(site, office, gpHead, costHead);

  const Stat = ({ l, v, tone, note }: { l: string; v: string; tone?: 'good' | 'bad'; note?: string }) => (
    <div className="rounded-2xl border border-border bg-card p-4">
      <p className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-muted-foreground">{l}</p>
      <p className={`mt-1 ds-kpi-md ${tone === 'good' ? 'text-success' : tone === 'bad' ? 'text-danger' : 'text-foreground'}`}>{v}</p>
      {note && <p className="mt-1 text-[11.5px] text-muted-foreground">{note}</p>}
    </div>
  );

  return (
    <div className="mx-auto w-full max-w-6xl">
      <FinanceNav />
      <Link to="/finance/forecast" className="mt-2 inline-flex items-center gap-1 text-[12px] text-muted-foreground no-underline hover:text-foreground">
        <ChevronLeft className="h-3.5 w-3.5" /> Forecast
      </Link>
      <h1 className="ds-h1 mt-1">Staff Mix</h1>
      <p className="mb-4 mt-0.5 text-[13px] text-muted-foreground">How your split of on-the-tools vs office staff drives profit.</p>

      <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-3">
        <div className="rounded-2xl border border-border bg-card p-4">
          <p className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.06em] text-muted-foreground">Your team</p>
          <div className="flex flex-col gap-2.5">
            <FinanceSlider label="Site (billable) staff" value={site} set={setSite} min={0} max={50} step={1} fmt={(v) => `${v}`} />
            <FinanceSlider label="Office (overhead) staff" value={office} set={setOffice} min={0} max={20} step={1} fmt={(v) => `${v}`} />
            <FinanceSlider label="GP per site head / yr" value={gpHead} set={setGpHead} min={40_000} max={300_000} step={5_000} fmt={fmtCurrency} />
            <FinanceSlider label="Cost per office head / yr" value={costHead} set={setCostHead} min={40_000} max={200_000} step={5_000} fmt={fmtCurrency} />
            {(() => {
              const maxV = Math.max(m.siteGp, m.officeCost) || 1;
              return (
                <div className="mt-1 border-t border-border pt-2.5">
                  <div className="mb-0.5 flex justify-between text-[11px] text-muted-foreground"><span>Site GP</span><span className="font-mono">{fmtCurrency(m.siteGp)}</span></div>
                  <div className="h-3 overflow-hidden rounded bg-muted"><div className="h-3 rounded bg-success/70" style={{ width: `${(m.siteGp / maxV) * 100}%` }} /></div>
                  <div className="mb-0.5 mt-1.5 flex justify-between text-[11px] text-muted-foreground"><span>Office cost</span><span className="font-mono">{fmtCurrency(m.officeCost)}</span></div>
                  <div className="h-3 overflow-hidden rounded bg-muted"><div className="h-3 rounded bg-warning/70" style={{ width: `${(m.officeCost / maxV) * 100}%` }} /></div>
                </div>
              );
            })()}
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:col-span-2">
          <Stat l="Net contribution / yr" v={fmtCurrency(m.netContribution)} tone={m.netContribution >= 0 ? 'good' : 'bad'} note={`${fmtCurrency(m.siteGp)} site GP − ${fmtCurrency(m.officeCost)} office cost`} />
          <Stat l="Billable ratio" v={`${m.siteRatioPct.toFixed(0)}%`} note={`${site} of ${site + office} heads generate revenue`} />
          <Stat l="Convert 1 office → site" v={`+${fmtCurrency(m.shiftDelta)}/yr`} tone="good" note="the annual profit swing from making one office role billable" />
        </div>
      </div>
      <p className="mt-4 text-[11px] text-muted-foreground">A planning calculator — enter your own headcount and per-head figures. Site staff earn GP; office staff are overhead to recover.</p>
      <ForecastToolNav current="staff-mix" />
    </div>
  );
}
